import { useCallback, useEffect, useRef, useState } from 'react';
import type { BattleStatus, DeviceApp, DeviceInfo, DeviceOs, LivePlayer, LiveScope, LiveView, OpsBattleRow, StateSnapshot } from '@shared/types';
import { CLOSE_AFTER_MS, DEFAULT_CLUE_INTERVAL_MS } from '@shared/engine';
import { staffApi, type StaffAuth } from '@/staff/auth';
import { api, serverNow } from './api';
import { connectBattle, type RtStatus } from './realtime';

// ---------------------------------------------------------------------------
// Data: the authenticated live views. Names never travel on the public realtime channel, so these pages poll.
// ---------------------------------------------------------------------------

/**
 * GET /battles/{code}/live with the host key (scope 'host'), a signed-in staff session, or the ops key (scope 'ops').
 * The host key goes as it always has; otherwise the session when signed in, else the ops key from the link.
 * `as: 'host'` (the host page) keeps the view to what the host scope shows, whatever the server answered with.
 */
export async function fetchLive(code: string, auth: { hostKey?: string; opsKey?: string; session?: string }, opts: { as?: LiveScope } = {}): Promise<LiveView> {
  const headers: Record<string, string> = {};
  let staff: StaffAuth = {};
  if (auth.hostKey) {
    if (auth.opsKey) headers['X-Ops-Key'] = auth.opsKey;
    headers['X-Host-Key'] = auth.hostKey;
  } else staff = auth.session ? { session: auth.session } : { opsKey: auth.opsKey };
  const v = await staffApi<Partial<LiveView>>(`/battles/${encodeURIComponent(code)}/live`, staff, { headers });
  if (!v.state) throw new Error('the live view came back without the battle state');
  // Fill any list the server left out, so a partial response can never blank the producer's page mid-stream.
  const view: LiveView = {
    scope: v.scope ?? (auth.opsKey || (auth.session && !auth.hostKey) ? 'ops' : 'host'), state: v.state, totals: { players: 0, answeredCurrent: 0, ...v.totals },
    devices: v.devices ?? {}, joinsPerMinute: v.joinsPerMinute ?? [], questions: v.questions ?? [], players: v.players ?? [],
    truncated: v.truncated, ...(v.sharing ? { sharing: v.sharing } : {}), ...(v.event ? { event: v.event } : {}), at: v.at ?? v.state.at,
  };
  return opts.as === 'host' ? toHostScope(view) : view;
}

/**
 * A live view cut down to the host scope (the server's rule): while a question is open, no option split and no chosen
 * option; no ops-only figures, devices or player details. Used on the host page when it is opened with a staff session.
 */
export function toHostScope(v: LiveView): LiveView {
  if (v.scope === 'host') return v;
  const open = v.state.status === 'question';
  const players = v.players.map((p): LivePlayer => {
    const { device: _d, ref: _r, marketing: _m, profileDone: _p, medSchool: _s, year: _y, current, ...rest } = p;
    if (!current) return rest;
    const { optionId, ...cur } = current;
    return { ...rest, current: open ? cur : { ...cur, ...(optionId !== undefined ? { optionId } : {}) } };
  });
  return {
    ...v, scope: 'host', devices: {}, players,
    totals: { players: v.totals.players, answeredCurrent: v.totals.answeredCurrent },
    questions: v.questions.map((q) => (q.open ? { ...q, counts: {} } : q)),
  };
}

/** GET /ops/battles: the most recent battles, newest first. Takes the session or the ops key (a bare string is the ops key). */
export async function fetchOpsBattles(auth: StaffAuth | string): Promise<OpsBattleRow[]> {
  const r = await staffApi<{ battles?: OpsBattleRow[] }>('/ops/battles', typeof auth === 'string' ? { opsKey: auth } : auth);
  return Array.isArray(r.battles) ? r.battles : [];
}

export interface Polled<T> { data?: T; error?: Error; updatedAt?: number }

/**
 * Calls `fn` at once, then every `intervalMs` while the page is visible. Pauses while hidden and calls again as soon as the page
 * is shown. Never starts a call while one is running. Keeps the last good data when a call fails. Starts afresh when `deps` change.
 */
export function usePolling<T>(fn: () => Promise<T>, intervalMs: number, deps: unknown[], opts: { enabled?: boolean } = {}): Polled<T> {
  const enabled = opts.enabled ?? true;
  const [state, setState] = useState<Polled<T>>({});
  const fnRef = useRef(fn);
  useEffect(() => { fnRef.current = fn; });

  useEffect(() => {
    setState((s) => (s.data === undefined && s.error === undefined && s.updatedAt === undefined ? s : {}));
    if (!enabled) return;
    let alive = true; let running = false; let timer: number | undefined;
    const visible = () => document.visibilityState === 'visible';
    const run = async () => {
      if (!alive || running) return;
      running = true; window.clearTimeout(timer); timer = undefined;
      const started = Date.now();
      try {
        const data = await fnRef.current();
        if (alive) setState({ data, updatedAt: Date.now() });
      } catch (e) {
        if (alive) setState((s) => ({ ...s, error: e instanceof Error ? e : new Error(String(e)) }));
      } finally {
        running = false;
        if (alive && visible()) timer = window.setTimeout(run, Math.max(0, intervalMs - (Date.now() - started)));
      }
    };
    const onVisibility = () => {
      if (!visible()) { window.clearTimeout(timer); timer = undefined; return; }
      if (!running) void run();
    };
    document.addEventListener('visibilitychange', onVisibility);
    void run();
    return () => { alive = false; window.clearTimeout(timer); document.removeEventListener('visibilitychange', onVisibility); };
  }, [intervalMs, enabled, ...deps]); // eslint-disable-line react-hooks/exhaustive-deps

  return state;
}

// ---------------------------------------------------------------------------
// The ops key: from the link's #key=…, remembered for this tab so a reload still works.
// ---------------------------------------------------------------------------
const OPS_KEY_STORE = 'rounds.ops.key';
const quietly = <T>(fn: () => T): T | undefined => { try { return fn(); } catch { return undefined; } };

export function readOpsKey(): string {
  const raw = /key=([^&]+)/.exec(window.location.hash)?.[1];
  const fromHash = raw ? (quietly(() => decodeURIComponent(raw)) ?? raw) : '';
  const key = fromHash || (quietly(() => sessionStorage.getItem(OPS_KEY_STORE)) ?? '');
  if (key) quietly(() => sessionStorage.setItem(OPS_KEY_STORE, key));
  return key;
}
/** A link that carries the key in its fragment, so it also works when opened in a new tab (signed in without a key: the path). */
export const opsHref = (path: string, key?: string) => (key ? `${path}#key=${encodeURIComponent(key)}` : path);
/** Signing out forgets the ops key this tab remembered too. */
export function forgetOpsKey(): void { quietly(() => sessionStorage.removeItem(OPS_KEY_STORE)); }

// ---------------------------------------------------------------------------
// Formatting shared by the producer roster and the ops pages.
// ---------------------------------------------------------------------------
export const fmt = (n: number) => n.toLocaleString('en-GB');
/** Whole percentage, or undefined when there is nothing to divide by. */
export const pct = (n: number, of: number): number | undefined => (of > 0 ? Math.round((100 * n) / of) : undefined);
export const seconds = (ms: number) => `${(ms / 1000).toFixed(1)} s`;

/** How long ago someone joined, for the lobby chips: "just now", "2 min", "1 h". */
export function sinceLabel(t: number, now: number): string {
  const s = Math.max(0, now - t) / 1000;
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  return m < 60 ? `${m} min` : `${Math.floor(m / 60)} h`;
}
/** When a battle was created: "just now", "12 min ago", "3 h ago", "2 days ago", then the date. */
export function agoLabel(t: number, now: number): string {
  const m = Math.floor(Math.max(0, now - t) / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h} h ago`;
  const d = Math.floor(h / 24); if (d < 7) return `${d} ${d === 1 ? 'day' : 'days'} ago`;
  return new Date(t).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}
/** Clock time in the viewer's zone, "19:42". */
export const clockTime = (t: number) => new Date(t).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

const OS: Record<DeviceOs, string> = { ios: 'iPhone', android: 'Android', desktop: 'Desktop', other: 'Other' };
const APP: Record<DeviceApp, string> = { instagram: 'Instagram', facebook: 'Facebook', tiktok: 'TikTok', browser: 'browser' };
/** "iPhone · Instagram", "Android · browser", "Desktop · browser", "Other". */
export function deviceLabel(d: DeviceInfo): string {
  const os = OS[d.os] ?? 'Other'; const app = APP[d.app] ?? 'browser';
  return os === 'Other' && app === 'browser' ? 'Other' : `${os} · ${app}`;
}
/** The live view counts devices under `${os}·${app}`, or 'unknown' for players who joined before devices were recorded. */
export function deviceKeyLabel(key: string): string {
  if (key === 'unknown') return 'Unknown';
  const [os, app] = key.split('·').map((s) => s.trim());
  return os && app ? deviceLabel({ os: os as DeviceOs, app: app as DeviceApp }) : key;
}
export const isInstagram = (key: string) => key.split('·')[1]?.trim() === 'instagram';

export const STATUS_LABEL: Record<BattleStatus, string> = { lobby: 'In the lobby', question: 'Question open', reveal: 'Answer shown', podium: 'Podium', ended: 'Ended' };

// ---------------------------------------------------------------------------
// The battle feed for the live views (host, ops, stage): realtime first, polling as the fallback, merged by version.
// ---------------------------------------------------------------------------

/**
 * Merges a snapshot into the current one the way the phones do: a newer version replaces it, keeping the question through its reveal
 * when the reveal does not repeat it; a fresher read of the same version (nothing is published on a join) only refreshes the player count.
 * Returns `cur` itself when nothing changes, so callers can skip a render.
 */
export function mergeSnapshot(cur: StateSnapshot | undefined, s: StateSnapshot): StateSnapshot | undefined {
  if (!cur || s.code !== cur.code) return s;
  if (s.version > cur.version) {
    const question = s.question ?? (s.status === 'reveal' && cur.index === s.index ? cur.question : undefined);
    return question && !s.question ? { ...s, question } : s;
  }
  if (s.version === cur.version && s.at > cur.at) return cur.playerCount === s.playerCount ? { ...cur, at: s.at } : { ...cur, playerCount: s.playerCount, at: s.at };
  return cur;
}

/** The battle's public state for a live view, with the realtime status. In the lobby the state is re-read every few seconds for the join count. */
export function useBattleFeed(code: string, opts: { lobbyPollMs?: number } = {}) {
  const lobbyPollMs = opts.lobbyPollMs ?? 3000;
  const [snap, setSnap] = useState<StateSnapshot>();
  const [conn, setConn] = useState<RtStatus>('connecting');
  const latest = useRef<StateSnapshot | undefined>(undefined);
  const take = useCallback((s: StateSnapshot) => {
    const m = mergeSnapshot(latest.current, s);
    if (m && m !== latest.current) { latest.current = m; setSnap(m); }
  }, []);
  useEffect(() => {
    latest.current = undefined; setSnap(undefined);
    if (!code) return;
    const rt = connectBattle(code, { onSnapshot: take, onStatus: setConn });
    return () => rt.close();
  }, [code, take]);
  useEffect(() => {
    if (snap?.status !== 'lobby' || !code) return;
    const t = window.setInterval(() => { api<{ state: StateSnapshot }>(`/battles/${code}/state`).then((r) => take(r.state)).catch(() => {}); }, lobbyPollMs);
    return () => window.clearInterval(t);
  }, [code, snap?.status, lobbyPollMs, take]);
  return { snap, conn, take };
}

// ---------------------------------------------------------------------------
// What the server should do next, and the tick backstop
// ---------------------------------------------------------------------------
/** Clues assumed for a 'clues' question whose snapshot does not say how many it deals (snapshots from before cluesTotal). */
export const MAX_CLUES = 5;
export type DueKind = 'start' | 'unveil' | 'clue' | 'close' | 'next';
export interface Due { at: number; kind: DueKind }

type OpenQuestion = NonNullable<StateSnapshot['question']>;
/** How many clues a 'clues' question deals in all: the snapshot's cluesTotal, never fewer than are already on the table. */
export const cluesTotalOf = (q: Pick<OpenQuestion, 'cluesTotal' | 'clues'>): number => Math.max(q.cluesTotal ?? MAX_CLUES, q.clues?.length ?? 0);
/** Clues on the table for the open question (the snapshot's cluesShown, else the clues it carries). */
export const cluesShownOf = (s: Pick<StateSnapshot, 'cluesShown' | 'question'>): number => s.cluesShown ?? s.question?.clues?.length ?? 0;

/** When the clues still to come fall due (server time): clue k at startsAt + k × interval, never at or after the deadline. */
export function clueTimes(s: StateSnapshot): number[] {
  const q = s.question;
  if (s.status !== 'question' || !q || q.format !== 'clues') return [];
  const every = q.clueIntervalMs && q.clueIntervalMs > 0 ? q.clueIntervalMs : DEFAULT_CLUE_INTERVAL_MS;
  const out: number[] = [];
  for (let k = cluesShownOf(s); k < cluesTotalOf(q); k++) { const at = q.startsAt + k * every; if (at >= q.deadlineAt) break; out.push(at); }
  return out;
}
/** The next clue's due time, if another clue is still to come. */
export const nextClueAt = (s: StateSnapshot): number | undefined => clueTimes(s)[0];

/**
 * Everything that will fall due on the server for this snapshot, earliest first (server time). 30 Oct 2026 (presenter
 * console): a delayed question still shown as its stub (text held until the unveil) has its unveil and its close due.
 */
export function dueTimes(s: StateSnapshot): Due[] {
  if (s.status === 'lobby') return s.mode === 'autopilot' && s.scheduledStartAt !== undefined && !s.hold ? [{ at: s.scheduledStartAt, kind: 'start' }] : [];
  if (s.status === 'question' && !s.question && s.upcoming) {
    return [{ at: s.upcoming.startsAt - (s.preRollMs ?? 0), kind: 'unveil' }, { at: s.upcoming.deadlineAt + CLOSE_AFTER_MS, kind: 'close' }];
  }
  if (s.status === 'question' && s.question) {
    return [...clueTimes(s).map((at): Due => ({ at, kind: 'clue' })), { at: s.question.deadlineAt + CLOSE_AFTER_MS, kind: 'close' }];
  }
  if (s.status === 'reveal') return s.mode === 'autopilot' && s.revealUntil !== undefined && !s.hold ? [{ at: s.revealUntil, kind: 'next' }] : [];
  return [];
}
export const nextDueAt = (s: StateSnapshot): number | undefined => dueTimes(s)[0]?.at;

const GRACE_MS = 800;          // let the autopilot do it first
const NOOP_LIMIT: Record<DueKind, number> = { start: 3, unveil: 3, clue: 2, close: 3, next: 3 };
const ERROR_LIMIT = 30;

/**
 * Backstop for the autopilot: once a due time has passed by the grace without a newer snapshot, POST /battles/{code}/tick once a second
 * (idempotent and server-gated) and take the state it returns. A due time that keeps coming back unchanged is dropped after a few tries,
 * so an empty clue time never blocks the close. If the tick route fails while a question is open, the close route is called instead.
 */
export function useTickBackstop(code: string, snap: StateSnapshot | undefined, take: (s: StateSnapshot) => void, opts: { enabled?: boolean } = {}) {
  const enabled = opts.enabled ?? true;
  const snapRef = useRef(snap); const takeRef = useRef(take);
  useEffect(() => { snapRef.current = snap; takeRef.current = take; });
  useEffect(() => {
    if (!enabled || !code) return;
    let alive = true; let busy = false;
    const noops = new Map<string, number>(); let errors = 0; let errorKey = '';
    const t = window.setInterval(async () => {
      const s = snapRef.current; if (!s || busy) return;
      const now = serverNow();
      const due = dueTimes(s).find((d) => (noops.get(`${s.version}:${d.at}`) ?? 0) < NOOP_LIMIT[d.kind]);
      if (!due || now < due.at + GRACE_MS) return;
      const key = `${s.version}:${due.at}`;
      if (key !== errorKey) { errorKey = key; errors = 0; }
      if (errors >= ERROR_LIMIT) return;
      busy = true;
      try {
        const r = await api<{ state: StateSnapshot }>(`/battles/${code}/tick`, { method: 'POST' });
        if (!alive) return;
        if (!r.state || r.state.version <= s.version) noops.set(key, (noops.get(key) ?? 0) + 1);
        if (r.state) takeRef.current(r.state);
      } catch {
        errors++;
        if (alive && s.status === 'question' && due.kind === 'close') await api(`/battles/${code}/close`, { method: 'POST' }).catch(() => {});
      } finally { busy = false; }
    }, 1000);
    return () => { alive = false; window.clearInterval(t); };
  }, [code, enabled]);
}

// ---------------------------------------------------------------------------
// Links
// ---------------------------------------------------------------------------
const origin = () => window.location.origin;
export const links = {
  join: (code: string) => `${origin()}/j/${code}`,
  stage: (code: string) => `${origin()}/stage/${code}`,
  /** The 9:16 stage for Instagram and TikTok (1080 × 1920). */
  stagePortrait: (code: string) => `${origin()}/stage/${code}?layout=portrait`,
  /** The battle's prize terms (general terms when it has no prize). */
  terms: (code: string) => `${origin()}/terms/${code}`,
  host: (code: string, secret: string) => `${origin()}/host/${code}#key=${encodeURIComponent(secret)}`,
  // Staff links carry the ops key when this tab has one; signed in without a key, they are plain (sign-in covers them).
  ops: (code: string, key?: string) => `${origin()}${opsHref(`/ops/${code}`, key)}`,
  opsList: (key?: string) => `${origin()}${opsHref('/ops', key)}`,
  studio: (key?: string) => `${origin()}${opsHref('/studio', key)}`,
  /** The Battle builder under the name people see (the studio route stays as an alias). */
  builder: (key?: string) => `${origin()}${opsHref('/builder', key)}`,
};
/** A link without its scheme, for reading out or printing: "rounds.example.com/j/ABCDE". */
export const displayUrl = (url: string) => url.replace(/^https?:\/\//, '');

/** A source tag for attribution (?ref=): letters, digits, dashes and underscores only, at most 32 characters. */
export const cleanRef = (raw?: string | null) => (raw ?? '').replace(/[^A-Za-z0-9_-]/g, '').slice(0, 32);
/** The link with ?ref=<tag> (or &ref=<tag>) added when there is a tag. */
export function withRef(url: string, ref?: string): string {
  const r = cleanRef(ref);
  return r ? `${url}${url.includes('?') ? '&' : '?'}ref=${r}` : url;
}
/** A parameter of the page's #fragment (host links carry key=…, and may carry ref=… and view=…). */
export function hashParam(name: string): string | undefined {
  const raw = new RegExp(`(?:^|[#&])${name}=([^&]*)`).exec(window.location.hash)?.[1];
  if (raw === undefined) return undefined;
  return quietly(() => decodeURIComponent(raw)) ?? raw;
}

/** The ops key this tab already holds (from an ops or studio link opened earlier), never read from the current link. */
export function knownOpsKey(): string { return quietly(() => sessionStorage.getItem(OPS_KEY_STORE)) ?? ''; }
